// FHIR
import Client from "fhir-kit-client";
// Translation
import i18n from "i18next";
// React
import { FunctionComponent, useCallback, useEffect, useMemo, useState } from "react";
// Navigation
import { useNavigate } from "react-router-dom";
// Components
import { FhirStatus, SearchableTable, SimpleCode, StatusTag, ValueSetLoader } from "@fyrstain/fhir-front-library";
import ApolloPage from "../../components/ApolloPage/ApolloPage";
// Font Awesome
import { faEye } from "@fortawesome/free-solid-svg-icons";
// Day js
import dayjs from "dayjs";

const DiagnosticReport: FunctionComponent = () => {

    /////////////////////////////////////
    //            Constants            //
    /////////////////////////////////////

    const otherCategoryUrl = process.env.REACT_APP_VALUESET_CATEGORY_URL ?? 'http://fyrstain.com/pdt/ValueSet/categories';
    const codesUrl = process.env.REACT_APP_VALUESET_CODE_URL ?? 'http://fyrstain.com/pdt/ValueSet/codes';

    /////////////////////////////////////
    //             State               //
    /////////////////////////////////////

    const [category] = useState(undefined as SimpleCode | undefined);
    const [code] = useState(undefined as SimpleCode | undefined);
    const [categories, setCategories] = useState([] as SimpleCode[]);
    const [codes, setCodes] = useState([] as SimpleCode[]);
    const diagnosticReportStatuses = ['registered', 'partial', 'preliminary', 'final'];
    const [refreshKey] = useState(0);

    /////////////////////////////////////
    //             Client              //
    /////////////////////////////////////

    const fhirClient = useMemo(() => new Client({
        baseUrl: process.env.REACT_APP_FHIR_URL ?? 'fhir'
    }), []);

    const valueSetLoader = useMemo(() => new ValueSetLoader(fhirClient), [fhirClient]);

    /////////////////////////////////////
    //           Navigation            //
    /////////////////////////////////////

    const navigate = useNavigate();

    const onDetails = useCallback(() => {
        navigate("/InProgress");
    }, [navigate]);

    /////////////////////////////////////
    //             Errors              //
    /////////////////////////////////////

    const onError = useCallback(() => {
        navigate("/Error");
    }, [navigate]);

    /////////////////////////////////////
    //          Page Loading           //
    /////////////////////////////////////

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        /**
         * Load the initial state of the page.
         */
        async function loadPage() {
            setLoading(true);
            try {
                setCategories(await valueSetLoader.searchValueSet(otherCategoryUrl));
                setCodes(await valueSetLoader.searchValueSet(codesUrl));
            } catch (error) {
                console.log(error);
                onError();
            }
            setLoading(false);
        }

        loadPage();
    }, [valueSetLoader, otherCategoryUrl, codesUrl, onError]);

    /**
     * Get the name of the patient with the given ID.
     *
     * @param id The ID of the patient
     * @returns The name of the patient
     */
    const getPatientName = async (id: string) => {
        const patientResource = await fhirClient.read({ resourceType: 'Patient', id: id });
        return patientResource.name[0].given.join(' ') + ' ' + patientResource.name[0].family;
    };

    /////////////////////////////////////
    //             Content             //
    /////////////////////////////////////

    /**
     * Get the option element to represent the code in an Input Select.
     * @param code the code.
     * @returns the option element.
     */
    function getOption(code: SimpleCode) {
        return { value: code.system + '|' + code.code, label: code.display ?? code.code };
    }

    /**
 * Get the option element to represent the status in an Input Select.
 * @param status the status.
 * @returns the option element.
 */
    function getStatusOption(status: string) {
        return { value: status, label: status };
    }

    return (
        <ApolloPage titleKey='title.diagnoticreports' loading={loading} needsLogin={true}>
            <>
                <SearchableTable
                    key={refreshKey}
                    searchCriteriaProperties={{
                        title: i18n.t('title.searchcriteria'),
                        submitButtonLabel: i18n.t('button.search'),
                        resetButtonLabel: i18n.t('button.reset'),
                        language: i18n.t,
                        fixedParameters:
                        {
                            _sort: "-_lastUpdated",
                        },
                        inputs: [
                            {
                                label: 'Patient',
                                type: "text",
                                placeholder: i18n.t('placeholder.patient'),
                                searchParamsName: "subject:Patient.name",
                            },
                            {
                                label: 'Date',
                                type: "date",
                                placeholder: i18n.t('placeholder.date'),
                                searchParamsName: "date:contains",
                            },
                            {
                                label: i18n.t('label.category'),
                                type: "select",
                                placeholder: i18n.t('defaultselectoption.category'),
                                options: categories.map(getOption),
                                searchParamsName: "category",
                            },
                            {
                                label: i18n.t('label.code'),
                                type: "select",
                                placeholder: i18n.t('defaultselectoption.code'),
                                options: codes.map(getOption),
                                searchParamsName: "code",
                            },
                            {
                                label: i18n.t('label.status'),
                                type: "select",
                                placeholder: i18n.t('defaultselectoption.status'),
                                options: diagnosticReportStatuses.map(getStatusOption),
                                searchParamsName: "status",
                            }
                        ],
                    }}
                    paginatedTableProperties={{
                        columns: [
                            {
                                header: 'Patient',
                                dataField: "patient",
                                width: "20%",
                                tabletWidth: "17%",
                            },
                            {
                                header: 'Date',
                                dataField: "date",
                                width: "20%",
                                tabletWidth: "17%",
                                formatter: (cell: string) => {
                                    return <>{dayjs(cell).fromNow()}</>;
                                },
                            },
                            {
                                header: i18n.t('label.category'),
                                dataField: "category",
                                width: "18%",
                                tabletWidth: "16%",
                            },
                            {
                                header: 'Code',
                                dataField: "code",
                                width: "18%",
                                tabletWidth: "16%",
                            },
                            {
                                header: i18n.t('label.status'),
                                dataField: "status",
                                width: "12%",
                                tabletWidth: "20%",
                                formatter: (cell: keyof typeof FhirStatus) => {
                                    return <StatusTag
                                        status={FhirStatus[cell]}
                                        statusMessage={cell}
                                    />;
                                },
                            },
                        ],
                        action: [
                            {
                                icon: faEye,
                                onClick: onDetails,
                            },
                        ],
                        mapResourceToData: async (resource: any) => {
                            const newDate = new Date(resource.effectiveDateTime);
                            console.log(resource.id);
                            return {
                                id: resource.id,
                                patient: resource.subject.identifier.value,
                                date: newDate,
                                category: resource.category[0]?.coding[0]?.display,
                                code: resource.code?.coding[0]?.display,
                                status: resource.status,
                            };
                        },
                        searchProperties: {
                            serverUrl: process.env.REACT_APP_FHIR_URL ?? 'fhir',
                            resourceType: 'DiagnosticReport',
                        },
                        onError: onError,
                    }}
                />
            </>
        </ApolloPage>
    );
};

export default DiagnosticReport;
